// Spotify API integration service
import type { SpotifyArtist, SpotifyAlbum } from '../../shared/types/entities';
import { supabase } from './supabase';

export class SpotifyService {
  private clientId: string;
  private clientSecret: string;

  constructor() {
    // Credentials are optional. When absent, service will use serverless endpoints or mock data.
    this.clientId = localStorage.getItem('spotify_client_id') || import.meta.env.VITE_SPOTIFY_CLIENT_ID || '';
    this.clientSecret = localStorage.getItem('spotify_client_secret') || import.meta.env.VITE_SPOTIFY_CLIENT_SECRET || '';
  }

  // Indicates whether credentials are available for live API calls
  isConfigured(): boolean {
    return Boolean(this.clientId && this.clientSecret);
  }

  // Access token flow removed from client. Use serverless endpoints or mock data instead.
  private async getAccessToken(): Promise<string> {
    throw new Error('Client-side Spotify token flow is disabled. Use serverless endpoints.');
  }

  // Parse Spotify URL to extract ID and type
  parseSpotifyUrl(url: string): { id: string; type: 'artist' | 'album' | 'track' } | null {
    const patterns = [
      // Standard Spotify URLs
      /spotify\.com\/(artist|album|track)\/([a-zA-Z0-9]+)/,
      // Spotify URI format
      /spotify:(artist|album|track):([a-zA-Z0-9]+)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        const [, type, id] = match;
        return { 
          id, 
          type: type as 'artist' | 'album' | 'track' 
        };
      }
    }

    return null;
  }

  // Get artist data via serverless endpoint or mock fallback
  async getArtist(artistId: string): Promise<SpotifyArtist> {
    try {
      const headers: HeadersInit = {};

      try {
        const { data } = await supabase?.auth?.getSession?.();
        const token = data?.session?.access_token;
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.warn('Unable to attach Supabase session for Spotify request:', error);
      }

      const resp = await fetch(`/api/spotify-artist?artistId=${encodeURIComponent(artistId)}`, {
        headers,
      });
      if (resp.ok) {
        const payload = await resp.json();
        // api/spotify-artist returns { images, artistProfile, syncedAt, mock? }
        if (payload?.artistProfile) {
          return Object.assign(payload.artistProfile, {
            __mock: payload.mock,
            syncedAt: payload.syncedAt,
          }) as SpotifyArtist;
        }
        if (payload?.id && payload?.name) {
          return Object.assign(payload, {
            __mock: payload.mock,
            syncedAt: payload.syncedAt,
          }) as SpotifyArtist;
        }
      }
      // Fallback to mock if serverless endpoint unavailable
      return this.getMockArtistData(artistId) as SpotifyArtist;
    } catch (error) {
      console.warn('Falling back to mock artist data:', error);
      return this.getMockArtistData(artistId) as SpotifyArtist;
    }
  }

  // Get artist's singles/tracks from Spotify
  async getArtistSingles(artistId: string, options: {
    market?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{
    items: SpotifyAlbum[];
    total: number;
    limit: number;
    offset: number;
  }> {
    try {
      // Use serverless API endpoint for better reliability
      const params = new URLSearchParams({
        artist_id: artistId,
        include_groups: 'single', // Only singles (individual tracks)
        market: options.market || 'US',
        limit: (options.limit || 50).toString(),
        offset: (options.offset || 0).toString()
      });
      
      const response = await fetch(`/api/spotify-artist-albums?${params}`);

      if (!response.ok) {
        throw new Error(`Spotify API error: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to fetch artist singles from Spotify:', error);
      
      // Return mock data as fallback
      return this.getMockArtistSingles(artistId);
    }
  }

  // Get album data from Spotify
  async getAlbum(albumId: string): Promise<SpotifyAlbum> {
    try {
      const token = await this.getAccessToken();
      
      const response = await fetch(`https://api.spotify.com/v1/albums/${albumId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Spotify API error: ${response.status}`);
      }

      const album = await response.json();
      
      // Get tracks if not included
      if (!album.tracks?.items) {
        const tracksResponse = await fetch(`https://api.spotify.com/v1/albums/${albumId}/tracks`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (tracksResponse.ok) {
          const tracksData = await tracksResponse.json();
          album.tracks = tracksData;
        }
      }

      return album;
    } catch (error) {
      console.error('Failed to fetch album from Spotify:', error);
      throw error;
    }
  }

  // Import artist data from Spotify URL
  async importArtistFromUrl(url: string): Promise<Partial<SpotifyArtist>> {
    const parsed = this.parseSpotifyUrl(url);
    
    if (!parsed || parsed.type !== 'artist') {
      throw new Error('Invalid Spotify artist URL');
    }


    try {
      // For development, return mock data if API not configured
      if (!this.clientId || !this.clientSecret) {
        return this.getMockArtistData(parsed.id);
      }

      const artistData = await this.getArtist(parsed.id);
      return {
        id: artistData.id,
        name: artistData.name,
        genres: artistData.genres,
        images: artistData.images,
        followers: artistData.followers,
        external_urls: artistData.external_urls,
        popularity: artistData.popularity
      };
    } catch (error) {
      console.error('Failed to import artist:', error);
      // Fallback to mock data if API fails
      console.warn('Falling back to mock data due to API error');
      return this.getMockArtistData(parsed.id);
    }
  }

  // Import release data from Spotify URL via serverless endpoint (preferred)
  async importReleaseFromUrl(url: string): Promise<{ album: any; track?: any }> {
    try {
      const resp = await fetch(`/api/spotify-release?url=${encodeURIComponent(url)}`);
      if (!resp.ok) throw new Error(`spotify_release_error_${resp.status}`);
      const json = await resp.json();
      if (json?.album) return { album: json.album, track: json.track };
      throw new Error('invalid_spotify_release_payload');
    } catch (e) {
      console.warn('Falling back to mock album data for importReleaseFromUrl');
      const parsed = this.parseSpotifyUrl(url);
      if (!parsed) throw e;
      const mock = this.getMockAlbumData(parsed.id);
      return { album: mock } as any;
    }
  }

  // Mock data for development
  private getMockArtistData(id: string): Partial<SpotifyArtist> {
    return {
      id,
      name: 'Sample Artist',
      genres: ['pop', 'indie'],
      images: [{ url: 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg', height: 640, width: 640 }],
      followers: { total: 50000 },
      external_urls: { spotify: `https://open.spotify.com/artist/${id}` },
      popularity: 65
    };
  }

  private getMockArtistSingles(artistId: string): {
    items: SpotifyAlbum[];
    total: number;
    limit: number;
    offset: number;
  } {
    // Generate mock singles for demo
    const singles = [
      {
        id: 'single1',
        name: 'Summer Vibes',
        album_type: 'single',
        total_tracks: 1,
        release_date: '2024-08-15',
        release_date_precision: 'day',
        images: [{ url: 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg', height: 640, width: 640 }],
        artists: [{ id: artistId, name: 'Sample Artist' }],
        external_urls: { spotify: `https://open.spotify.com/album/single1` },
        popularity: 85
      },
      {
        id: 'single2', 
        name: 'Midnight Dreams',
        album_type: 'single',
        total_tracks: 1,
        release_date: '2024-06-20',
        release_date_precision: 'day',
        images: [{ url: 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg', height: 640, width: 640 }],
        artists: [{ id: artistId, name: 'Sample Artist' }],
        external_urls: { spotify: `https://open.spotify.com/album/single2` },
        popularity: 78
      },
      {
        id: 'single3',
        name: 'Neon Lights', 
        album_type: 'single',
        total_tracks: 1,
        release_date: '2024-04-10',
        release_date_precision: 'day',
        images: [{ url: 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg', height: 640, width: 640 }],
        artists: [{ id: artistId, name: 'Sample Artist' }],
        external_urls: { spotify: `https://open.spotify.com/album/single3` },
        popularity: 72
      },
      {
        id: 'single4',
        name: 'Heartbreak Hotel', 
        album_type: 'single',
        total_tracks: 1,
        release_date: '2024-02-14',
        release_date_precision: 'day',
        images: [{ url: 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg', height: 640, width: 640 }],
        artists: [{ id: artistId, name: 'Sample Artist' }],
        external_urls: { spotify: `https://open.spotify.com/album/single4` },
        popularity: 68
      },
      {
        id: 'single5',
        name: 'Electric Soul', 
        album_type: 'single',
        total_tracks: 1,
        release_date: '2023-12-01',
        release_date_precision: 'day',
        images: [{ url: 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg', height: 640, width: 640 }],
        artists: [{ id: artistId, name: 'Sample Artist' }],
        external_urls: { spotify: `https://open.spotify.com/album/single5` },
        popularity: 65
      }
    ] as unknown as SpotifyAlbum[];
    
    return {
      items: singles,
      total: singles.length,
      limit: 50,
      offset: 0
    };
  }

  private getMockAlbumData(id: string): Partial<SpotifyAlbum> {
    return {
      id,
      name: 'Sample Album',
      album_type: 'album',
      total_tracks: 12,
      release_date: '2024-01-15',
      release_date_precision: 'day',
      images: [{ url: 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg', height: 640, width: 640 }],
      artists: [{ id: 'artist123', name: 'Sample Artist' }],
      external_urls: { spotify: `https://open.spotify.com/album/${id}` },
      tracks: {
        items: Array.from({ length: 12 }, (_, i) => ({
          id: `track${i + 1}`,
          name: `Track ${i + 1}`,
          duration_ms: 180000 + (Math.random() * 120000),
          track_number: i + 1
        }))
      }
    };
  }
}

// Export singleton instance
export const spotifyService = new SpotifyService();
